import React, { useState, useMemo } from "react";
import { Input } from "../ui/input";
import { Button } from "../ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../ui/popover";
import {
  ChevronDown,
  ChevronRight,
  Search,
  Check,
} from "lucide-react";

const norm = (v) => String(v ?? "").toLowerCase().trim();

// ============== Helper: Flat Search Results ==============
const SearchResults = ({
  search,
  roles,
  roleCategories,
  departments,
  value,
  onPick,
}) => {
  const q = norm(search);

  const matched = useMemo(() => {
    return roles.filter((r) => {
      if (norm(r.label).includes(q)) return true;

      const cat = roleCategories.find(
        (c) => norm(c.value) === norm(r.category)
      );
      if (cat && norm(cat.label).includes(q)) return true;

      const dept = departments.find(
        (d) => norm(d.value) === norm(r.department)
      );
      if (dept && norm(dept.label).includes(q)) return true;

      return false;
    });
  }, [q, roles, roleCategories, departments]);

  if (matched.length === 0) {
    return (
      <p className="p-3 text-sm text-gray-500">
        No roles found for "{search}"
      </p>
    );
  }

  return (
    <div>
      <p className="text-xs text-gray-500 px-2 py-1">
        {matched.length} result{matched.length > 1 ? "s" : ""}
      </p>

      {matched.map((role) => {
        const cat =
          roleCategories.find(
            (c) => norm(c.value) === norm(role.category)
          ) || {
            value: role.category,
            label: role.category,
            department: role.department,
          };

        const dept = departments.find(
          (d) => norm(d.value) === norm(role.department)
        );

        const isActive = norm(role.value) === norm(value);

        return (
          <button
            type="button"
            key={role.value}
            onClick={() => onPick(role, cat)}
            className={`w-full text-left flex items-start justify-between gap-2 px-2 py-2 rounded hover:bg-blue-50 ${
              isActive ? "bg-blue-100 text-blue-700" : ""
            }`}
          >
            <div className="flex flex-col">
              <span className="text-sm font-medium">{role.label}</span>
              <span className="text-xs text-gray-500">
                {dept?.label || "—"} › {cat?.label || "—"}
              </span>
            </div>
            {isActive && <Check className="w-4 h-4 text-blue-600 mt-1" />}
          </button>
        );
      })}
    </div>
  );
};

// ============== Main Component ==============
const DepartmentRolePicker = ({
  departments = [],
  roleCategories = [],
  roles = [],
  value = "",
  onChange = () => {},
  placeholder = "Select Department & Role",
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [activeDept, setActiveDept] = useState(null);
  const [expandedCats, setExpandedCats] = useState({});

  // ============== Selected role lookup ==============
  const selectedRole = useMemo(
    () => roles.find((r) => norm(r.value) === norm(value)),
    [roles, value]
  );

  const selectedDeptLabel = useMemo(() => {
    if (!selectedRole) return "";
    const dept = departments.find(
      (d) => norm(d.value) === norm(selectedRole.department)
    );
    return dept?.label || "";
  }, [selectedRole, departments]);

  // ============== Filter departments by search ==============
  const filteredDepartments = useMemo(() => {
    if (!search) return departments;
    const q = norm(search);

    const matchedFromRoles = new Set();
    roles.forEach((r) => {
      if (norm(r.label).includes(q)) {
        matchedFromRoles.add(norm(r.department));
      }
    });

    const matchedFromCats = new Set();
    roleCategories.forEach((c) => {
      if (norm(c.label).includes(q)) {
        matchedFromCats.add(norm(c.department));
      }
    });

    return departments.filter(
      (d) =>
        norm(d.label).includes(q) ||
        matchedFromCats.has(norm(d.value)) ||
        matchedFromRoles.has(norm(d.value))
    );
  }, [search, departments, roleCategories, roles]);

  // ============== Categories for active dept ==============
  const deptCategories = useMemo(() => {
    if (!activeDept) return [];
    return roleCategories.filter(
      (c) => norm(c.department) === norm(activeDept.value)
    );
  }, [activeDept, roleCategories]);

  // ============== Get roles for category ==============
  const getCategoryRoles = (catVal) =>
    roles.filter((r) => norm(r.category) === norm(catVal));

  // ============== Selection handler ==============
  const handleSelectRole = (role, cat) => {
    onChange({
      role: role.value,
      roleLabel: role.label,
      category: cat.value,
      categoryLabel: cat.label,
      department: cat.department,
      departmentLabel:
        departments.find((d) => norm(d.value) === norm(cat.department))
          ?.label || "",
    });
    setOpen(false);
    setSearch("");
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          disabled={disabled}
          className="w-full justify-between font-normal h-10"
        >
          {selectedRole ? (
            <span className="text-sm truncate">
              <span className="text-gray-500">{selectedDeptLabel}</span>
              {selectedDeptLabel && " › "}
              <span className="font-medium">{selectedRole.label}</span>
            </span>
          ) : (
            <span className="text-gray-500 text-sm">{placeholder}</span>
          )}
          <ChevronDown className="w-4 h-4 opacity-60" />
        </Button>
      </PopoverTrigger>

      <PopoverContent
        className="p-0 w-[600px] max-w-[90vw]"
        align="start"
      >
        {/* ============== Search Bar ============== */}
        <div className="relative p-3 border-b">
          <Search className="absolute left-6 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            autoFocus
            placeholder="Search Department or Role..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9 h-9"
          />
        </div>

        {/* ============== Picker Body ============== */}
        <div className="grid grid-cols-2 max-h-[400px]">
          {/* LEFT — Departments (hidden on small screens while searching) */}
          <div
            className={`border-r overflow-y-auto bg-gray-50 ${
              search ? "hidden md:block" : ""
            }`}
          >
            {filteredDepartments.map((dept) => (
              <button
                key={dept.value}
                type="button"
                onClick={() => setActiveDept(dept)}
                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between hover:bg-blue-50 ${
                  activeDept?.value === dept.value
                    ? "bg-blue-100 font-medium text-blue-700"
                    : ""
                }`}
              >
                <span className="truncate">{dept.label}</span>
                <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
              </button>
            ))}
            {filteredDepartments.length === 0 && (
              <p className="p-3 text-sm text-gray-500">No departments</p>
            )}
          </div>

          {/* RIGHT — Search Results OR Categories+Roles */}
          <div
            className={`overflow-y-auto p-2 ${
              search ? "col-span-2 md:col-span-1" : ""
            }`}
          >
            {search ? (
              // ============== SEARCH MODE: flat role list ==============
              <SearchResults
                search={search}
                roles={roles}
                roleCategories={roleCategories}
                departments={departments}
                value={value}
                onPick={handleSelectRole}
              />
            ) : !activeDept ? (
              <p className="p-3 text-sm text-gray-500">
                Select a department from left
              </p>
            ) : (
              <>
                {deptCategories.map((cat) => {
                  const catRoles = getCategoryRoles(cat.value);
                  const expanded = expandedCats[cat.value] ?? true;

                  return (
                    <div key={cat.value} className="mb-2">
                      <button
                        type="button"
                        className="w-full flex items-center gap-1 px-2 py-1 hover:bg-gray-100 rounded text-left"
                        onClick={() =>
                          setExpandedCats((p) => ({
                            ...p,
                            [cat.value]: !expanded,
                          }))
                        }
                      >
                        {expanded ? (
                          <ChevronDown className="w-4 h-4 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-4 h-4 text-gray-500" />
                        )}
                        <span className="text-sm font-medium">
                          {cat.label}
                        </span>
                        <span className="text-xs text-gray-400 ml-auto">
                          {catRoles.length}
                        </span>
                      </button>

                      {expanded && (
                        <div className="ml-6">
                          {catRoles.map((role) => {
                            const isActive =
                              norm(role.value) === norm(value);
                            return (
                              <button
                                type="button"
                                key={role.value}
                                onClick={() =>
                                  handleSelectRole(role, cat)
                                }
                                className={`w-full text-left flex items-center justify-between gap-2 px-2 py-1 text-sm rounded hover:bg-blue-50 ${
                                  isActive
                                    ? "bg-blue-100 text-blue-700 font-medium"
                                    : ""
                                }`}
                              >
                                <span>{role.label}</span>
                                {isActive && (
                                  <Check className="w-4 h-4 text-blue-600" />
                                )}
                              </button>
                            );
                          })}

                          {catRoles.length === 0 && (
                            <p className="text-xs text-gray-400 px-2 py-1">
                              No roles
                            </p>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}

                {deptCategories.length === 0 && (
                  <p className="p-3 text-sm text-gray-500">
                    No categories for this department
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default DepartmentRolePicker;